import { useAppSelector } from 'src/store';
import { useForm } from 'react-hook-form';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useGetZohoEmailSetupMutation } from 'src/services';
import { Skeleton } from 'src/components/ui/skeleton';
import { LoaderWrapper } from 'src/components/LoaderWrapper/LoaderWrapper';
import { useLoader } from 'src/provider/LoaderProvider';
import { useNotification } from 'src/hooks/useNotification';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { ghcolors } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { Button } from 'src/components/ui/button';
import { SETUP_GUIDE_URL } from 'src/constants/urls';

export const ZohoEmailStep3 = ({
  onClick = (_d?: any) => {},
  onClose = (_v?: any) => {},
}: {
  onClick?: (d?: any) => void;
  onClose?: (v?: any) => void;
}) => {
  const [code, setCode] = useState('');
  const { id } = useParams();
  const loader = useLoader();
  const notification = useNotification();
  const { selectedIntegration } = useAppSelector((s) => s.integrationSlices);
  const [getZohoEmailSetup, { data: setupResponse, isLoading: isLoadingZohoEmailSetup }] =
    useGetZohoEmailSetupMutation() as any;

  useEffect(() => {
    if (id && selectedIntegration.id) {
      getZohoEmailSetup({
        projectId: id,
        integrationId: selectedIntegration.id,
      });
    }
  }, [id, selectedIntegration]);

  useEffect(() => {
    if (setupResponse && setupResponse.custom_function_code) {
      setCode(setupResponse.custom_function_code);
    }
  }, [setupResponse]);

  useEffect(() => {
    if (isLoadingZohoEmailSetup) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingZohoEmailSetup]);

  const { handleSubmit } = useForm();

  const submit = () => {
    onClose(true);
  };

  const copyToClipboard = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    await navigator.clipboard.writeText(code);

    notification.success('Copied to clipboard');
  };

  return (
    <LoaderWrapper
      loader={<Skeleton className="w-full h-16" />}
      isLoading={isLoadingZohoEmailSetup}
    >
      <form
        id="zoho-email-step-3-form"
        className={'w-full overflow-hidden flex flex-col'}
        onSubmit={handleSubmit(submit)}
      >
        <div className={'w-full flex flex-col gap-2.5 p-5 bg-light rounded-sm-5'}>
          <>
            <div className={'flex flex-row justify-between items-center'}>
              <p className={'text-ebony font-semibold text-sm'}>Instruction:</p>
            </div>
            <ul className={'list-decimal px-3'}>
              <li>
                <p className={'text-sm text-ebony_opacity_70 font-medium'}>
                  Before you begin, copy and save the code snippet below — you’ll need it when
                  creating a Plug in Zoho Desk.
                </p>
                <p className={'py-1.5'}>
                  <label>Custom Function Code:</label>
                </p>
                <div className={'overflow-hidden bg-white'}>
                  <SyntaxHighlighter
                    language="javascript" // closest syntax to Deluge
                    style={ghcolors}
                    customStyle={{
                      borderRadius: '3px',
                      padding: '16px',
                      fontSize: '14px',
                      width: '100%',
                      height: '10rem',
                      overflow: 'auto',
                      marginBottom: '0',
                      marginTop: '0',
                    }}
                  >
                    {code}
                  </SyntaxHighlighter>
                </div>
                <Button className={'px-8 mt-2.5 mb-4'} onClick={copyToClipboard} disabled={!code}>
                  Copy
                </Button>
              </li>
              <li>
                <p className={'text-sm text-ebony_opacity_70'}>
                  Once you do, follow our{' '}
                  <a
                    target={'_blank'}
                    className={'text-ebony italic font-semibold hover:underline'}
                    href={SETUP_GUIDE_URL.ZOHODESK_EMAIL}
                    rel="noopener noreferrer"
                  >
                    Zoho Desk Setup Guide
                  </a>{' '}
                  to complete the integration.
                </p>
              </li>
            </ul>
          </>
        </div>
      </form>
    </LoaderWrapper>
  );
};
